/**
 * Hook for fetching and managing protocol detail data
 * (team members, bookmarks, external contacts)
 */

import { useState, useCallback, useEffect } from 'react';
import api from '../../services/api';
import type {
  Protocol,
  StudyTeamMember,
  StudyBookmark,
  BookmarkAssignment,
  ExternalContact,
  SiteUser,
  AddTeamMemberFormData,
  AddBookmarkFormData,
  AddContactFormData,
} from '../types';

interface UseProtocolDetailOptions {
  protocolId: string;
  autoFetch?: boolean;
}

interface UseProtocolDetailReturn {
  // Data
  protocol: Protocol | null;
  teamMembers: StudyTeamMember[];
  bookmarks: StudyBookmark[];
  contacts: ExternalContact[];
  siteUsers: SiteUser[];

  // Loading states
  loading: boolean;
  teamLoading: boolean;
  bookmarksLoading: boolean;
  contactsLoading: boolean;

  // Error
  error: string | null;

  // Protocol actions
  fetchProtocol: () => Promise<void>;
  updateProtocol: (data: Partial<Protocol>) => Promise<void>;

  // Team member actions
  fetchTeamMembers: () => Promise<void>;
  addTeamMember: (data: AddTeamMemberFormData) => Promise<StudyTeamMember>;
  updateTeamMember: (memberId: string, data: Partial<StudyTeamMember>) => Promise<void>;
  removeTeamMember: (memberId: string) => Promise<void>;

  // Bookmark actions
  fetchBookmarks: () => Promise<void>;
  addBookmark: (data: AddBookmarkFormData) => Promise<StudyBookmark>;
  updateBookmark: (bookmarkId: string, data: Partial<StudyBookmark>) => Promise<void>;
  removeBookmark: (bookmarkId: string) => Promise<void>;
  reorderBookmarks: (orderedIds: string[]) => Promise<void>;

  // Bookmark assignment actions (per-link portal access)
  assignments: BookmarkAssignment[];
  fetchAssignments: () => Promise<void>;
  assignBookmarkUser: (bookmarkId: string, userId: string) => Promise<void>;
  unassignBookmarkUser: (bookmarkId: string, userId: string) => Promise<void>;

  // Contact actions
  fetchContacts: () => Promise<void>;
  addContact: (data: AddContactFormData) => Promise<ExternalContact>;
  updateContact: (contactId: string, data: Partial<ExternalContact>) => Promise<void>;
  removeContact: (contactId: string) => Promise<void>;

  // Site users (for team member dropdown)
  fetchSiteUsers: (siteId: string) => Promise<void>;
}

export function useProtocolDetail({ protocolId, autoFetch = true }: UseProtocolDetailOptions): UseProtocolDetailReturn {
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [teamMembers, setTeamMembers] = useState<StudyTeamMember[]>([]);
  const [bookmarks, setBookmarks] = useState<StudyBookmark[]>([]);
  const [assignments, setAssignments] = useState<BookmarkAssignment[]>([]);
  const [contacts, setContacts] = useState<ExternalContact[]>([]);
  const [siteUsers, setSiteUsers] = useState<SiteUser[]>([]);

  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(false);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch protocol
  const fetchProtocol = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/siteorganizer/protocols/${protocolId}`);
      setProtocol(response.data);

      // Also fetch site users for team member dropdown. Dropdown data must not blank the page,
      // so its failure is swallowed here rather than surfaced via the outer catch.
      if (response.data.site_id) {
        try {
          const usersResponse = await api.get(`/api/siteorganizer/site/${response.data.site_id}/users`);
          setSiteUsers(usersResponse.data);
        } catch (usersErr: any) {
          console.error('Error fetching site users:', usersErr);
        }
      }
    } catch (err: any) {
      console.error('Error fetching protocol:', err);
      setError(err.response?.data?.error || 'Failed to fetch protocol');
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  // Update protocol
  const updateProtocol = useCallback(async (data: Partial<Protocol>) => {
    const response = await api.patch(`/api/siteorganizer/protocols/${protocolId}`, data);
    setProtocol(response.data);
  }, [protocolId]);

  // =====================================================
  // TEAM MEMBERS
  // =====================================================

  const fetchTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const response = await api.get(`/api/siteorganizer/protocols/${protocolId}/team`);
      setTeamMembers(response.data);
    } catch (err: any) {
      console.error('Error fetching team members:', err);
    } finally {
      setTeamLoading(false);
    }
  }, [protocolId]);

  const addTeamMember = useCallback(async (data: AddTeamMemberFormData): Promise<StudyTeamMember> => {
    const response = await api.post(`/api/siteorganizer/protocols/${protocolId}/team`, data);
    setTeamMembers(prev => [...prev, response.data]);
    return response.data;
  }, [protocolId]);

  const updateTeamMember = useCallback(async (memberId: string, data: Partial<StudyTeamMember>) => {
    const response = await api.patch(`/api/siteorganizer/protocols/${protocolId}/team/${memberId}`, data);
    setTeamMembers(prev => prev.map(m => m.id === memberId ? { ...m, ...response.data } : m));
  }, [protocolId]);

  const removeTeamMember = useCallback(async (memberId: string) => {
    await api.delete(`/api/siteorganizer/protocols/${protocolId}/team/${memberId}`);
    setTeamMembers(prev => prev.filter(m => m.id !== memberId));
  }, [protocolId]);

  // =====================================================
  // BOOKMARKS
  // =====================================================

  const fetchBookmarks = useCallback(async () => {
    setBookmarksLoading(true);
    try {
      const response = await api.get(`/api/siteorganizer/protocols/${protocolId}/bookmarks`);
      setBookmarks(response.data);
    } catch (err: any) {
      console.error('Error fetching bookmarks:', err);
    } finally {
      setBookmarksLoading(false);
    }
  }, [protocolId]);

  const addBookmark = useCallback(async (data: AddBookmarkFormData): Promise<StudyBookmark> => {
    const response = await api.post(`/api/siteorganizer/protocols/${protocolId}/bookmarks`, data);
    setBookmarks(prev => [...prev, response.data]);
    return response.data;
  }, [protocolId]);

  const updateBookmark = useCallback(async (bookmarkId: string, data: Partial<StudyBookmark>) => {
    const response = await api.patch(`/api/siteorganizer/protocols/${protocolId}/bookmarks/${bookmarkId}`, data);
    setBookmarks(prev => prev.map(b => b.id === bookmarkId ? { ...b, ...response.data } : b));
  }, [protocolId]);

  const removeBookmark = useCallback(async (bookmarkId: string) => {
    await api.delete(`/api/siteorganizer/protocols/${protocolId}/bookmarks/${bookmarkId}`);
    setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    // Assignments cascade server-side; mirror locally.
    setAssignments(prev => prev.filter(a => a.bookmark_id !== bookmarkId));
  }, [protocolId]);

  // Saves the study's bookmark order (shared by everyone on the study). The new
  // order shows at once; on failure the server's order is reloaded and the
  // error is rethrown for the tab to show.
  const reorderBookmarks = useCallback(async (orderedIds: string[]) => {
    setBookmarks(prev => {
      const byId = new Map(prev.map(b => [b.id, b]));
      const next = orderedIds.map(id => byId.get(id)).filter((b): b is StudyBookmark => Boolean(b));
      return next.length === prev.length ? next : prev;
    });
    try {
      const response = await api.put(`/api/siteorganizer/protocols/${protocolId}/bookmarks/order`, { ids: orderedIds });
      setBookmarks(response.data);
    } catch (err) {
      fetchBookmarks();
      throw err;
    }
  }, [protocolId, fetchBookmarks]);

  // =====================================================
  // BOOKMARK ASSIGNMENTS (per-link portal access)
  // =====================================================

  const fetchAssignments = useCallback(async () => {
    try {
      const response = await api.get(`/api/siteorganizer/protocols/${protocolId}/bookmark-assignments`);
      setAssignments(response.data);
    } catch (err: any) {
      console.error('Error fetching bookmark assignments:', err);
    }
  }, [protocolId]);

  const assignBookmarkUser = useCallback(async (bookmarkId: string, userId: string) => {
    const response = await api.post(
      `/api/siteorganizer/protocols/${protocolId}/bookmarks/${bookmarkId}/assignments`,
      { user_id: userId }
    );
    setAssignments(prev => [
      ...prev.filter(a => !(a.bookmark_id === bookmarkId && a.user_id === userId)),
      response.data,
    ]);
  }, [protocolId]);

  const unassignBookmarkUser = useCallback(async (bookmarkId: string, userId: string) => {
    await api.delete(`/api/siteorganizer/protocols/${protocolId}/bookmarks/${bookmarkId}/assignments/${userId}`);
    setAssignments(prev => prev.filter(a => !(a.bookmark_id === bookmarkId && a.user_id === userId)));
  }, [protocolId]);

  // =====================================================
  // CONTACTS
  // =====================================================

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const response = await api.get(`/api/siteorganizer/protocols/${protocolId}/contacts`);
      setContacts(response.data);
    } catch (err: any) {
      console.error('Error fetching contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [protocolId]);

  const addContact = useCallback(async (data: AddContactFormData): Promise<ExternalContact> => {
    const response = await api.post(`/api/siteorganizer/protocols/${protocolId}/contacts`, data);
    setContacts(prev => [...prev, response.data]);
    return response.data;
  }, [protocolId]);

  const updateContact = useCallback(async (contactId: string, data: Partial<ExternalContact>) => {
    const response = await api.patch(`/api/siteorganizer/protocols/${protocolId}/contacts/${contactId}`, data);
    setContacts(prev => prev.map(c => c.id === contactId ? { ...c, ...response.data } : c));
  }, [protocolId]);

  const removeContact = useCallback(async (contactId: string) => {
    await api.delete(`/api/siteorganizer/protocols/${protocolId}/contacts/${contactId}`);
    setContacts(prev => prev.filter(c => c.id !== contactId));
  }, [protocolId]);

  // Fetch site users
  const fetchSiteUsers = useCallback(async (siteId: string) => {
    try {
      const response = await api.get(`/api/siteorganizer/site/${siteId}/users`);
      setSiteUsers(response.data);
    } catch (err: any) {
      console.error('Error fetching site users:', err);
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && protocolId) {
      fetchProtocol();
      fetchTeamMembers();
      fetchBookmarks();
      fetchAssignments();
      fetchContacts();
    }
  }, [autoFetch, protocolId, fetchProtocol, fetchTeamMembers, fetchBookmarks, fetchAssignments, fetchContacts]);

  return {
    protocol,
    teamMembers,
    bookmarks,
    contacts,
    siteUsers,
    loading,
    teamLoading,
    bookmarksLoading,
    contactsLoading,
    error,
    fetchProtocol,
    updateProtocol,
    fetchTeamMembers,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    fetchBookmarks,
    addBookmark,
    updateBookmark,
    removeBookmark,
    reorderBookmarks,
    assignments,
    fetchAssignments,
    assignBookmarkUser,
    unassignBookmarkUser,
    fetchContacts,
    addContact,
    updateContact,
    removeContact,
    fetchSiteUsers,
  };
}

export default useProtocolDetail;
